import { AppBreadcrumb, AppCard, AppSpace } from '@/components/atoms';
import { CreateClinicUserForm } from '@/components/organisms';
import type { ClinicUserInitialData } from '@/components/organisms/CreateClinicUserForm';

import { LoadingTemplate, SiteAdminNotFoundTemplate } from '@/components/templates';
import { useAdminClinic } from '@/hooks/admin/useAdminClinics';
import { useClinicUserQuery } from '@/hooks/admin/useClinicUserQuery';
import { useUpdateClinicUserMutation } from '@/hooks/admin/useUpdateClinicUserMutation';
import useAppToast from '@/hooks/useAppToast';
import type { ClinicUserEditFormData, ClinicUserFormData } from '@/schemas/clinicUserSchema';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuditInfo } from '@/components/molecules';
import { normalizeOptionalDate } from '@/utils/dateConverter';

type ApiFieldErrors = Partial<Record<keyof ClinicUserEditFormData, string>>;

interface ApiValidationErrorResponse {
  message?: string;
  errors?: Record<string, string[]>;
}

const EditClinicUserPage: React.FC = () => {
  const toast = useAppToast();
  const navigate = useNavigate();
  const { site, id } = useParams<{ site: string; id: string }>();
  const { currentSite } = useSiteAdminAuth();
  const siteType = site ?? currentSite ?? '';
  const clinicUserId = id ? parseInt(id, 10) : 0;

  const { data: clinicUserData, isLoading, isError } = useClinicUserQuery(siteType, clinicUserId);
  const clinicIdFromUser = clinicUserData?.data?.clinic_id ?? 0;
  const { data: clinicData } = useAdminClinic(siteType, clinicIdFromUser);
  const updateUserMutation = useUpdateClinicUserMutation();
  const queryClient = useQueryClient();
  const [apiErrors, setApiErrors] = useState<ApiFieldErrors>({});

  const initialData: ClinicUserInitialData | null = useMemo(() => {
    const data = clinicUserData?.data;
    if (!data) return null;
    return {
      id: data.id,
      clinic_id: data.clinic_id,
      role: data.role,
      login_id: data.login_id,
      password: data.password,
      name: data.name,
      email: data.email != null ? String(data.email).trim() : null,
      available_from: data.available_from,
      available_to: data.available_to,
    };
  }, [clinicUserData]);

  const clinicId = initialData?.clinic_id ?? 0;

  const lockedClinicDisplayName = useMemo(() => {
    if (!clinicData?.data) return null;
    const data: any = clinicData.data;
    const infos = Array.isArray(data.clinic_informations) ? data.clinic_informations : null;
    if (infos && infos.length > 0) {
      const latest = infos.reduce(
        (max: any, curr: any) => (curr.id > max.id ? curr : max),
        infos[0]
      );
      return latest?.name ?? data.clinic_number ?? null;
    }
    return data.clinic_number ?? null;
  }, [clinicData]);

  const breadcrumbItems = [
    {
      title: 'Home',
      href: `/${siteType}/admin`,
    },
    {
      title: '治療院詳細',
      href: `/${siteType}/admin/clinics/${clinicIdFromUser}`,
    },
    {
      title: `編集:${id}`,
    },
  ];

  const handleSubmit = async (formData: ClinicUserFormData | ClinicUserEditFormData) => {
    if (!siteType || !clinicUserId) {
      toast.error('サイト情報が取得できませんでした。');
      return;
    }

    // Clear previous API errors
    setApiErrors({});

    try {
      await updateUserMutation.mutateAsync({
        siteType,
        id: clinicUserId,
        data: {
          role: formData.role,
          login_id: formData.login_id,
          password: formData.password && formData.password.trim() !== '' ? formData.password : null,
          name: formData.name,
          email: formData.email && formData.email.trim() !== '' ? formData.email : null,
          available_from: normalizeOptionalDate(formData.available_from),
          available_to: normalizeOptionalDate(formData.available_to),
        },
      });
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'clinic-users', siteType, clinicUserId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'clinics', siteType, clinicId, 'clinic-admin'],
      });
      toast.success('更新しました。');
      window.scrollTo(0, 0);
    } catch (err) {
      const axiosError = err as AxiosError<ApiValidationErrorResponse>;
      const responseErrors = axiosError.response?.data?.errors;

      if (responseErrors) {
        // Map API errors to field errors (take first error message for each field)
        const fieldErrors: ApiFieldErrors = {};
        for (const [field, messages] of Object.entries(responseErrors)) {
          if (messages && messages.length > 0) {
            fieldErrors[field as keyof ClinicUserEditFormData] = messages[0];
          }
        }
        setApiErrors(fieldErrors);
      } else {
        toast.error('エラーが発生しました。');
      }
    }
  };

  if (isLoading) {
    return <LoadingTemplate />;
  }

  if (isError || !initialData) {
    return <SiteAdminNotFoundTemplate />;
  }

  return (
    <div className="p-6">
      <AppSpace orientation="vertical" className="w-full" size="large">
        <AppBreadcrumb items={breadcrumbItems} />
        <AppCard title={`治療院ユーザー編集: ${id}`} className="w-full">
          <CreateClinicUserForm
            key={`edit-${initialData.id}`}
            siteType={siteType}
            clinicId={clinicId}
            lockedClinicDisplayName={lockedClinicDisplayName}
            clinicSiteType={clinicData?.data?.site_type ?? null}
            onSubmit={handleSubmit}
            onCancel={() => navigate(`/${siteType}/admin/clinics/${clinicId}`)}
            isLoading={updateUserMutation.isPending}
            apiErrors={apiErrors}
            initialData={initialData}
            isEditMode
          />
          <AuditInfo
            createdBy={clinicUserData?.data?.created_by}
            updatedBy={clinicUserData?.data?.updated_by}
            updatedAt={clinicUserData?.data?.updated_at}
          />
        </AppCard>
      </AppSpace>
    </div>
  );
};

export default EditClinicUserPage;
